import { createColumnHelper } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';

import { ScrollableCellWithAvatar } from '@/components/data-table/cells/scrollable-cell';
import { Button } from '@/components/ui/button';
import i18n from '@/core/i18n';
import { formatTableDate } from '@/lib/constants/table-display';
import { AllowedAction } from '@/modules/common/types/allowed-actions';

import { ChangedFieldsCell } from '../components/time-offs/changes/changed-fields-cell';
import { TimeOffTypeChangeLogEntry } from '../types/time-off-changes';

const columnHelper = createColumnHelper<TimeOffTypeChangeLogEntry>();

interface GetTimeOffChangesColumnsParams {
  onDeleteClick: (change: TimeOffTypeChangeLogEntry) => void;
  isDeleting: boolean;
}

/**
 * Columns for the time-off type Changes table.
 */
export const getTimeOffChangesColumns = ({
  onDeleteClick,
  isDeleting,
}: GetTimeOffChangesColumnsParams) => [
  columnHelper.accessor('startDate', {
    header: i18n.t('authenticated.settings:pages.timeOff.details.changesTab.table.startDate'),
    minSize: 100,
    size: 100,
    cell: ({ getValue }) => formatTableDate(getValue()),
  }),
  columnHelper.accessor('endDate', {
    header: i18n.t('authenticated.settings:pages.timeOff.details.changesTab.table.endDate'),
    minSize: 100,
    size: 100,
    cell: ({ getValue }) => formatTableDate(getValue()),
  }),
  columnHelper.accessor('changedBy', {
    header: i18n.t('authenticated.settings:pages.timeOff.details.changesTab.table.changedBy'),
    cell: ({ getValue }) => {
      const changedBy = getValue();
      if (!changedBy) {
        return (
          <span className="text-sm text-muted-foreground">{i18n.t('common:table.noValue')}</span>
        );
      }
      return (
        <ScrollableCellWithAvatar
          text={changedBy.name}
          avatarName={changedBy.name}
          image={changedBy.profileImageUrl ?? undefined}
        />
      );
    },
  }),
  columnHelper.accessor('changedFields', {
    header: () => (
      <div className="pl-4">
        {i18n.t('authenticated.settings:pages.timeOff.details.changesTab.table.changedFields')}
      </div>
    ),
    size: 350,
    minSize: 300,
    cell: ({ getValue }) => <ChangedFieldsCell changedFields={getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    minSize: 100,
    header: () => (
      <div className="w-full text-center">
        {i18n.t('authenticated.settings:pages.timeOff.details.changesTab.table.action')}
      </div>
    ),
    cell: ({ row }) => {
      if (!row.original.allowedActions.includes(AllowedAction.DELETE)) return null;

      return (
        <div className="flex items-center justify-end">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDeleteClick(row.original)}
            disabled={isDeleting}
            aria-label={i18n.t('common:delete')}
          >
            <Trash2 />
          </Button>
        </div>
      );
    },
  }),
];
